// ─────────────────────────────────────────────
//  BreakEnginePanel — 4-channel break controls for ONE provider (D-05)
//  UI layer ONLY: every transition goes through the existing tested
//  src/domain/breaks.js state machine — no break rules are re-implemented
//  here. Persists room-doc break state via setRoomBreak/endRoomBreak and,
//  for give-a-break, opens/closes a /breaks collection record (C-003).
// ─────────────────────────────────────────────
import React, { useState } from "react";
import tokens from "../theme/tokens";
import Button from "./Button";
import Card from "./Card";
import BreakStatusRow from "./BreakStatusRow";
import RelieverPicker from "./RelieverPicker";
import { useToast } from "../context/ToastContext";
import {
  BREAK_CHANNELS,
  getChannelState,
  isMeal,
  isBathroom,
  isBeingRelieved,
  isCoverCommitted,
  selfBreak,
  requestBathroom,
  assignCover,
  cancelCover,
  completeBreak,
  breakStatusRow,
} from "../domain/breaks";
import {
  setRoomBreak,
  endRoomBreak,
  requestBreak,
  updateBreak,
} from "../firebase/firestore";

const { color, font, space, radius } = tokens;

// Guarded matchMedia read (BreakStatusRow pattern).
function prefersReducedMotion() {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

// Map a state-machine throw to the UI-SPEC error copy.
function transitionErrorMessage(err, channel) {
  const msg = String(err?.message || "");
  if (/already in progress/i.test(msg)) return "A bathroom break is already in progress.";
  if (/already done/i.test(msg)) return `${channel ? channel[0].toUpperCase() + channel.slice(1) : "This"} break is already completed.`;
  return "This break action isn't available right now.";
}

const PILL_STYLES = {
  requested: { background: "#fef8ec", border: "1px solid #d97706", color: "#7a4f00", text: "Requested" },
  in_progress: { background: "#fef8ec", border: "1px solid #d97706", color: "#7a4f00", text: "On Break" },
  done: { background: "#e6f8f3", border: "1px solid #10b981", color: "#0a7d5e", text: "Done" },
};

function StatePill({ state, reduceMotion }) {
  const meta = PILL_STYLES[state];
  if (!meta) return <span style={{ opacity: 0.4, color: color.text.muted }} aria-hidden="true">—</span>;
  const flashing = (state === "requested" || state === "in_progress") && !reduceMotion;
  return (
    <span
      style={{
        ...st.pill,
        background: meta.background,
        border: meta.border,
        color: meta.color,
        ...(flashing ? { animation: "atcFlash 1s steps(1) infinite" } : null),
      }}
    >
      {meta.text}
    </span>
  );
}

export default function BreakEnginePanel({
  provider,
  breaks,
  roomId,
  roster = [],
  facilityId,
  onBreaksChange,
}) {
  const toast = useToast();
  const reduceMotion = prefersReducedMotion();
  const [pickerChannel, setPickerChannel] = useState(null);
  const [busy, setBusy] = useState(false);
  // /breaks record ids opened by give-a-break, per channel — closed on done.
  const [openBreakIds, setOpenBreakIds] = useState({});

  // Reliever candidates: not currently covering anyone, not departed, not self.
  const candidates = roster.filter(
    // ⚠️ `isCoverCommitted`, NOT `isBeingRelieved`. This reads the CANDIDATE'S
    // OWN record and asks "is this person spoken for". Somebody already walking
    // to another room is not free, even though nobody is relieving them —
    // asking the narrower question offers the same person to two rooms.
    (m) => m.id !== provider?.id && !m.departed && !isCoverCommitted(m.breaks || {})
  );

  // `onBreak` follows the STATE, never the fact that a write happened: naming a
  // cover must not outline the room or, through coverage.js, declare the on-call
  // OB CRNA uncovered "for a break" before anybody has walked anywhere.
  const persistRoomBreak = async (next, relieverId = null, relieverName = "", channel = null) => {
    await setRoomBreak(roomId, {
      breaks: next, channel, relieverId, relieverName,
      onBreak: isBeingRelieved(next),
    });
    onBreaksChange && onBreaksChange(next);
  };

  const handleSelfBreak = async (channel) => {
    setBusy(true);
    try {
      const next = selfBreak(breaks, channel); // domain machine owns the rules
      await persistRoomBreak(next);
      toast.success(`${provider?.name || "Provider"} is on ${channel} break.`);
    } catch (err) {
      toast.error(transitionErrorMessage(err, channel));
    } finally {
      setBusy(false);
    }
  };

  const handleRequestBathroom = async () => {
    setBusy(true);
    try {
      const next = requestBathroom(breaks);
      await persistRoomBreak(next);
    } catch (err) {
      toast.error(transitionErrorMessage(err, "bathroom"));
    } finally {
      setBusy(false);
    }
  };

  const handleGiveBreak = async (channel, reliever) => {
    setPickerChannel(null);
    setBusy(true);
    try {
      const next = assignCover(breaks, channel);
      // (a) live room-doc state the board renders from
      await persistRoomBreak(next, reliever.id, reliever.name, channel);
      // (b) the durable /breaks coverage record (C-003 / SC-5)
      const ref = await requestBreak({
        facilityId: facilityId || null,
        roomId,
        type: channel,
        giverId: provider?.id || null,
        giverName: provider?.name || "",
        receiverId: reliever.id,
        receiverName: reliever.name,
        // Nothing has started — somebody was asked. See domain/breaks.
        assignedAt: new Date().toISOString(),
      });
      if (ref?.id) setOpenBreakIds((prev) => ({ ...prev, [channel]: ref.id }));
    } catch (err) {
      toast.error(transitionErrorMessage(err, channel));
    } finally {
      setBusy(false);
    }
  };

  // Stand a named cover down before they arrive. Returns the channel to where
  // the ask came from — NEVER to `done`, which would lock a meal green for a
  // provider who never left the room.
  const handleCancelCover = async (channel) => {
    setBusy(true);
    try {
      const next = cancelCover(breaks, channel, { wasRequested: isBathroom(channel) });
      await persistRoomBreak(next, null, "", channel);
      const breakId = openBreakIds[channel];
      if (breakId) {
        await updateBreak(breakId, { status: "cancelled", cancelledAt: new Date().toISOString() });
        setOpenBreakIds((prev) => {
          const nextIds = { ...prev };
          delete nextIds[channel];
          return nextIds;
        });
      }
      toast.info(`Cover for ${channel} was called off.`);
    } catch (err) {
      toast.error(transitionErrorMessage(err, channel));
    } finally {
      setBusy(false);
    }
  };

  const handleBreakDone = async (channel) => {
    setBusy(true);
    try {
      const next = completeBreak(breaks, channel); // meal locks, bathroom resets
      await endRoomBreak(roomId, { breaks: next });
      onBreaksChange && onBreaksChange(next);
      // Close the /breaks record only when give-a-break opened one.
      const breakId = openBreakIds[channel];
      if (breakId) {
        await updateBreak(breakId, { status: "completed", completedAt: new Date().toISOString() });
        setOpenBreakIds((prev) => {
          const { [channel]: _closed, ...rest } = prev;
          return rest;
        });
      }
    } catch (err) {
      toast.error(transitionErrorMessage(err, channel));
    } finally {
      setBusy(false);
    }
  };

  const channelActions = (channel) => {
    const state = getChannelState(breaks, channel);
    if (state === "assigned") {
      // Named, not here yet. The only write on offer is the arrival, and only
      // the named cover may make it — this panel is a shared board device, so
      // it offers the stand-down instead.
      return (
        <Button size="sm" variant="secondary" onClick={() => handleCancelCover(channel)} disabled={busy}
          ariaLabel={`Call off the ${channel} cover`}>
          Call off cover
        </Button>
      );
    }
    if (state === "in_progress") {
      return (
        <Button size="sm" onClick={() => handleBreakDone(channel)} disabled={busy}
          ariaLabel={`Mark ${channel} break done`}>
          Break Done
        </Button>
      );
    }
    if (state === "requested") {
      return (
        <Button size="sm" onClick={() => setPickerChannel(channel)} disabled={busy}
          ariaLabel={`Give ${channel} break`}>
          Give Break
        </Button>
      );
    }
    if (state === "done") return null; // meal locked — no actions
    // state === "none"
    if (isMeal(channel)) {
      return (
        <>
          <Button size="sm" onClick={() => handleSelfBreak(channel)} disabled={busy}
            ariaLabel={`Take ${channel} break`}>
            Take Break
          </Button>
          <Button size="sm" variant="secondary" onClick={() => setPickerChannel(channel)} disabled={busy}
            ariaLabel={`Give ${channel} break`}>
            Give Break
          </Button>
        </>
      );
    }
    return (
      <Button size="sm" onClick={handleRequestBathroom} disabled={busy}
        ariaLabel="Request bathroom break">
        Request Bathroom
      </Button>
    );
  };

  return (
    <Card style={{ marginBottom: space.md }}>
      <div style={st.header}>
        <h3 style={st.providerName}>{provider?.name || "Provider"}</h3>
      </div>
      <BreakStatusRow row={breakStatusRow(breaks)} />

      <div style={{ marginTop: space.md }}>
        {BREAK_CHANNELS.map((channel) => {
          const state = getChannelState(breaks, channel);
          const locked = state === "done" && isMeal(channel);
          return (
            <div key={channel} style={{ ...st.channelRow, ...(locked ? { opacity: 0.6 } : null) }}>
              <span style={st.channelLabel}>{channel}</span>
              <StatePill state={state} reduceMotion={reduceMotion} />
              <span style={st.actions}>{channelActions(channel)}</span>
            </div>
          );
        })}
      </div>

      {pickerChannel && (
        <RelieverPicker
          channel={pickerChannel}
          providerName={provider?.name || ""}
          candidates={candidates}
          onConfirm={(reliever) => handleGiveBreak(pickerChannel, reliever)}
          onCancel={() => setPickerChannel(null)}
        />
      )}
    </Card>
  );
}

const st = {
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  providerName: {
    margin: 0,
    fontSize: font.size.body,
    fontWeight: font.weight.bold,
    color: color.text.primary,
  },
  channelRow: {
    display: "flex",
    alignItems: "center",
    gap: space.sm,
    minHeight: 44, // touch target
    borderBottom: `1px solid ${color.border.default}`,
    padding: `${space.xs}px 0`,
  },
  channelLabel: {
    width: 80,
    fontSize: font.size.label,
    fontWeight: font.weight.bold,
    color: color.text.secondary,
    textTransform: "capitalize",
    flexShrink: 0,
  },
  pill: {
    borderRadius: radius.full,
    padding: "2px 10px",
    fontSize: font.size.label,
    fontWeight: font.weight.bold,
    whiteSpace: "nowrap",
  },
  actions: {
    display: "inline-flex",
    gap: space.sm,
    marginLeft: "auto",
  },
};
